/* ============================================================
   FLG HOLD CARD v1.1, 2026-09-13.
   Puts the address on a real link and a Hold control with the
   three real tiers on EVERY parcel card, everywhere on the site.

   Jigar Mehta's feedback, 2026-09-12, relayed by Harpreet:
     "Instead of view parcel, make the address as a link"
     "change the view parcel button to Hold (with a dropdown)"
     "grid view or split view, anywhere across website"

   v1.1 adds the title shortener. See shortTitle below.

   ------------------------------------------------------------
   WHY THIS IS ONE FILE AND NOT TWO TEMPLATE EDITS
   ------------------------------------------------------------
   Parcel cards are rendered by two different bits of code with
   two different markups:

     catalogue rail AND map pin popups
       assets/flg-map.js cardHTML()
       a.card > .shot + .txt(.cty, h3, .row, .foot(.amt, .go))

     homepage grid
       sections/flg-inventory.liquid
       a.card > .shot + .body(.county, h3.name, .att, .foot(.amt,.acre))

   They agree on the four hooks this file needs: `a.card`, `.tag`,
   `h3` and `.foot`. So this upgrades the DOM rather than editing
   either template, which means one implementation instead of two
   that drift apart, and it picks up any future card that uses the
   same hooks without being told about it.

   ------------------------------------------------------------
   WHY THE ANCHOR HAS TO BE REPLACED
   ------------------------------------------------------------
   The card today is ONE <a> wrapping the photo, the title and the
   price. A <button> or a <select> inside an <a> is invalid HTML:
   the click lands on the anchor, and screen readers announce a
   link containing a button. There is no CSS trick around it.

   So the <a class="card"> becomes <div class="card">, the h3 gets
   a real <a> with the address as its text, and that link's
   ::after is stretched across the whole card. Net effect: the
   address is a genuine link with a genuine accessible name, the
   whole card is still tappable exactly as before, and the Hold
   control sits above the stretched layer on its own z-index so it
   is reachable by mouse, thumb and keyboard.

   flg-map.js repaints .cards wholesale on every filter change and
   Leaflet builds popup DOM on open, so a MutationObserver picks
   those up. Cards already upgraded are marked and skipped.

   ------------------------------------------------------------
   THE TIERS ARE FETCHED, NOT BAKED IN
   ------------------------------------------------------------
   Shopify serves /products/<handle>.js for free. Verified
   2026-09-13: 200, and it returns every variant id, title, price
   in cents and availability. So the card feed did NOT have to be
   rebuilt to carry hold variants, which would have meant a rewrite
   of the 21,827 byte sections/flg-map.liquid and the 24,145 byte
   sections/flg-inventory.liquid.

   It is fetched on FIRST OPEN, never on page load. Nine parcels
   would otherwise be nine requests of about 42KB each for a panel
   most visitors never open.

   All nine for-sale parcels carry the same three tiers, verified
   against the Admin API on 2026-09-13:
     48-Hour Priority Hold   $50
     4-Day Diligence Hold    $100
     8-Day Executive Hold    $200
   The duration shown on each row is the leading token of the
   variant title, the same rule sections/flg-parcel-hold.liquid
   uses, so the card, the cart line and the order all agree. Rename
   a tier on the product, not here.

   ------------------------------------------------------------
   THE DISCLOSURE IS NOT DECORATION. DO NOT REMOVE IT.
   ------------------------------------------------------------
   A hold is NON-REFUNDABLE and it credits toward the $495 document
   fee. On the parcel page that sentence sits directly above the
   button. A card has no room for it in the resting state, so it
   is printed inside the panel, above the commit button, where it
   cannot be missed before money moves.

   Taking $200 of non-refundable money off a thumbnail with no
   terms on screen is a chargeback argument nobody wants to have.
   If the copy needs to change, change TERMS below, do not delete
   it.

   ------------------------------------------------------------
   SOLD AND HELD PARCELS
   ------------------------------------------------------------
   Gated twice, on purpose. The cheap check reads the card's own
   state: the map cards carry .tag.is-hold / .tag.is-closed, and
   any card whose tag reads hold, closed, sold or reserved is
   skipped, which covers the homepage markup too.

   The authoritative check is the fetch. If the product comes back
   with no available variant the panel says so instead of offering
   tiers, because inventory can change between the page rendering
   and the thumb landing. There is exactly one of each parcel.
   ============================================================ */
(function () {
  'use strict';

  var TERMS = 'Non-refundable, and it credits in full toward your $495 document fee. Placing a hold does not commit you to buy.';
  var LEDE = 'How long do you need';
  var OPEN_LABEL = 'Hold';
  var GO_LABEL = 'Place hold';
  var BUSY_LABEL = 'Adding…';
  var GONE = 'This parcel is no longer available to hold.';
  var FAIL = 'That did not go through. Try again, or call and we will take it off the market for you.';
  var DEAD = /\b(hold|closed|sold|reserved)\b/i;

  var cache = {};
  var openPanel = null;

  function money(cents) {
    var n = cents / 100;
    return '$' + (n % 1 === 0 ? n.toLocaleString('en-US') : n.toFixed(2));
  }

  /* The big duration is the leading token of the variant title,
     matching sections/flg-parcel-hold.liquid. "4-Day Diligence
     Hold" gives "4 Day". A title with no leading number renders
     whole rather than breaking. */
  function duration(title) {
    var m = String(title || '').match(/^\s*(\d+)\s*[-\s]\s*([A-Za-z]+)/);
    return m ? m[1] + ' ' + m[2] : String(title || '');
  }

  function handleFrom(href) {
    var m = String(href || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  /* ------------------------------------------------------------
     CARD TITLES ARE SEO TITLES AND THEY DO NOT FIT. v1.1.
     ------------------------------------------------------------
     The product title is written for Google:

       "0.47 Acres on Peebles Rd, Interlachen | Putnam County,
        FL Land for Sale"                        71 characters

     Clamped to two lines in a rail card measured at 145px wide on
     an iPad, that rendered as "PARCEL | 0.23 ACRE...", which
     tells a buyer nothing at all. Widening the card fixes most of
     it, but a 71 character title still eats two lines everywhere.

     The human half is what comes before the first pipe. Where
     there is no pipe, it is what comes before the first " - ".
     Everything after either of those is keyword tail:

       0.47 Acres on Peebles Rd, Interlachen
       THE BELLE STREET PARCEL
       The Darter Ave Corner
       0.23 Acres on Rockford Circle

     The full title is NOT lost. It is still the H1 of the parcel
     page and still what Google indexes. Only the card shows the
     short form, and the short form is also a better accessible
     name than 71 characters of keywords read aloud.

     The guards matter: if the cut is under 6 characters, or the
     title has neither separator, the original is left alone
     rather than mangled.
     ------------------------------------------------------------ */
  function shortTitle(t) {
    var s = String(t || '').trim();
    var cut = s.split('|')[0].trim();
    if (cut === s) cut = s.split(' - ')[0].trim();
    return (cut.length >= 6 && cut.length < s.length) ? cut : s;
  }

  function fetchProduct(href) {
    var h = handleFrom(href);
    if (!h) return Promise.reject(new Error('no handle'));
    if (cache[h]) return cache[h];
    cache[h] = fetch('/products/' + h + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) {
      if (!r.ok) throw new Error('status ' + r.status);
      return r.json();
    }).catch(function (e) {
      delete cache[h];
      throw e;
    });
    return cache[h];
  }

  function closeOpen() {
    if (!openPanel) return;
    openPanel.panel.hidden = true;
    openPanel.btn.setAttribute('aria-expanded', 'false');
    openPanel = null;
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function renderTiers(panel, data, href) {
    var body = panel.querySelector('[data-flgh-body]');
    body.textContent = '';

    var live = (data.variants || []).filter(function (v) { return v.available; });
    if (!live.length) {
      body.appendChild(el('p', 'flgh-c__msg', GONE));
      var back = el('a', 'flgh-c__msglink', 'See the parcel');
      back.href = href;
      body.appendChild(back);
      panel.querySelector('[data-flgh-go]').hidden = true;
      panel.querySelector('[data-flgh-terms]').hidden = true;
      return;
    }

    var name = 'flgh-tier-' + Math.random().toString(36).slice(2, 8);
    var list = el('ul', 'flgh-c__tiers');

    live.forEach(function (v, i) {
      var li = el('li');
      var lab = el('label', 'flgh-c__tier');
      var input = document.createElement('input');
      input.type = 'radio';
      input.name = name;
      input.value = String(v.id);
      input.className = 'flgh-c__radio';
      if (i === 0) input.checked = true;
      lab.appendChild(input);
      lab.appendChild(el('span', 'flgh-c__dur', duration(v.title)));
      lab.appendChild(el('span', 'flgh-c__amt', money(v.price)));
      li.appendChild(lab);
      list.appendChild(li);
    });

    body.appendChild(list);
    panel.querySelector('[data-flgh-go]').hidden = false;
    panel.querySelector('[data-flgh-terms]').hidden = false;
  }

  function addToCart(id) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    }).then(function (r) {
      if (!r.ok) {
        return r.json().then(function (j) {
          throw new Error((j && j.description) || 'add failed');
        });
      }
      return r.json();
    });
  }

  function buildControl(href) {
    var wrap = el('div', 'flgh-c__hold');

    var btn = el('button', 'flgh-c__btn');
    btn.type = 'button';
    btn.setAttribute('aria-expanded', 'false');
    btn.appendChild(el('span', null, OPEN_LABEL));
    btn.insertAdjacentHTML('beforeend',
      '<svg class="flgh-c__chev" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg>');

    var panel = el('div', 'flgh-c__panel');
    panel.hidden = true;
    panel.appendChild(el('p', 'flgh-c__lede', LEDE));

    var body = el('div', 'flgh-c__body');
    body.setAttribute('data-flgh-body', '');
    panel.appendChild(body);

    var terms = el('p', 'flgh-c__terms', TERMS);
    terms.setAttribute('data-flgh-terms', '');
    panel.appendChild(terms);

    var go = el('button', 'flgh-c__go', GO_LABEL);
    go.type = 'button';
    go.setAttribute('data-flgh-go', '');
    panel.appendChild(go);

    wrap.appendChild(btn);
    wrap.appendChild(panel);

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var isOpen = !panel.hidden;
      closeOpen();
      if (isOpen) return;

      panel.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      openPanel = { panel: panel, btn: btn };

      if (!panel.dataset.loaded) {
        body.textContent = 'Loading…';
        go.hidden = true;
        terms.hidden = true;
        fetchProduct(href).then(function (data) {
          panel.dataset.loaded = '1';
          renderTiers(panel, data, href);
        }).catch(function () {
          body.textContent = '';
          body.appendChild(el('p', 'flgh-c__msg', FAIL));
        });
      }
    });

    go.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var picked = panel.querySelector('.flgh-c__radio:checked');
      if (!picked) return;
      go.disabled = true;
      go.textContent = BUSY_LABEL;
      addToCart(picked.value).then(function () {
        window.location.href = '/cart';
      }).catch(function () {
        go.disabled = false;
        go.textContent = GO_LABEL;
        var msg = panel.querySelector('.flgh-c__msg');
        if (!msg) {
          body.appendChild(el('p', 'flgh-c__msg', FAIL));
        } else {
          msg.textContent = FAIL;
        }
      });
    });

    /* Clicks inside the panel must never reach the stretched
       card link underneath. */
    panel.addEventListener('click', function (e) { e.stopPropagation(); });

    return wrap;
  }

  function looksDead(card) {
    if (card.querySelector('.tag.is-hold, .tag.is-closed')) return true;
    var tag = card.querySelector('.tag');
    if (tag && DEAD.test(tag.textContent || '')) return true;
    return false;
  }

  function upgrade(a) {
    if (!a || a.dataset.flghUp === '1') return;
    var href = a.getAttribute('href') || '';
    if (!handleFrom(href)) return;

    var h3 = a.querySelector('h3');
    var foot = a.querySelector('.foot');
    if (!h3 || !foot) return;

    a.dataset.flghUp = '1';

    /* a -> div, same classes, same children, so every existing
       rule that targets .card keeps applying. */
    var div = document.createElement('div');
    div.className = a.className + ' flgh-c';
    if (a.dataset.id) div.dataset.id = a.dataset.id;
    div.dataset.flghUp = '1';
    while (a.firstChild) div.appendChild(a.firstChild);

    /* The address becomes the link, and its hit area is stretched
       back over the whole card by .flgh-c__link::after in the CSS,
       so nothing about tapping the photo changes. */
    var link = document.createElement('a');
    link.className = 'flgh-c__link';
    link.href = href;
    link.textContent = shortTitle(h3.textContent);
    h3.textContent = '';
    h3.appendChild(link);

    if (!looksDead(div)) {
      foot.appendChild(buildControl(href));
      div.classList.add('flgh-c--sale');
    }

    a.parentNode.replaceChild(div, a);
  }

  function sweep(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var list = scope.querySelectorAll('a.card[href*="/products/"]');
    for (var i = 0; i < list.length; i++) upgrade(list[i]);
  }

  document.addEventListener('click', function (e) {
    if (!openPanel) return;
    if (e.target && e.target.closest && e.target.closest('.flgh-c__hold')) return;
    closeOpen();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openPanel) {
      var b = openPanel.btn;
      closeOpen();
      b.focus();
    }
  });

  function start() {
    sweep(document);
    if (!window.MutationObserver) return;
    var t = null;
    new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(function () { sweep(document); }, 60);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
